import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, GlobalStyles, Typography } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import LogoutIcon from '@mui/icons-material/Logout';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PersonIcon from '@mui/icons-material/Person';
import Media from '@core/dashboard/components/AppLoader/Media';
import UserProfileModal from '@components/UserProfileModalNew';
import ParticipantService from '../../Dolby/ParticipantService';
import { MytaverseLogger } from '@helpers/logger';
import { entryPromptTimeout } from '@constants/entryPrompt';
import { useMytaverse } from '@providers/MytaverseProvider';
import { LeaveEventReason } from '@providers/MytaverseProvider/interfaces';
import { useMytaverseEvent } from '../../../../providers';
import { useMytaStreams } from '../MytaStreamsProvider';
import { useCinemaProgress } from './useCinemaProgress';
import { useMytaStreamsStage } from '../useStreamStage';
import { buildCinemaPalette } from './palette';
import { getCinemaStyles, cinemaGlobalStyles } from './styles';
import ControlsGuide from './ControlsGuide';
import {
  CINEMA_FONTS_LINK_ID,
  CINEMA_FONTS_URL,
  CINEMA_PHASES,
  CINEMA_TIPS,
  ETA_IMMINENT_LABEL,
  KEEP_TAB_HINT,
  LOADING_HEADLINE,
  READY_HEADLINE,
  READY_HINT,
  READY_STEP,
  RECONNECT_HEADLINE,
  RECONNECT_HINT,
  RECONNECT_PHASES,
  RECONNECT_READY_HEADLINE,
  RECONNECT_READY_HINT,
  SEGMENT_GAP_PX,
  SPOT_NOTICE_TAG,
  SPOT_NOTICE_TEXT,
  STEP_FADE_MS,
  TIP_FADE_MS,
  TIP_ROTATE_MS,
} from './constants';

const formatEta = (totalSec: number): string => {
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;

  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const CINEMA_SEGMENTS = CINEMA_PHASES.map((phase, i, phases) => {
  const start = phase.threshold;
  const end = phases[i + 1]?.threshold ?? 100;

  return {
    label: phase.label,
    start,
    end,
    left: `calc(${start}% + ${i === 0 ? 0 : SEGMENT_GAP_PX / 2}px)`,
    right: `calc(${100 - end}% + ${
      i === phases.length - 1 ? 0 : SEGMENT_GAP_PX / 2
    }px)`,
  };
});

const LoadingCinema = () => {
  const { appTheme, user, navigateToEventsPage } = useMytaverse();
  const { currentEvent, setShowControls } = useMytaverseEvent();
  const { togglePlayStream, playStream, reconnecting, entryNotice } =
    useMytaStreams();
  const {
    percent,
    subscribe,
    phaseIdx,
    done,
    etaSec,
    error,
    hintOverride,
    onSpot,
  } = useCinemaProgress();

  useMytaStreamsStage();

  const palette = useMemo(() => buildCinemaPalette(appTheme), [appTheme]);
  const classes = useMemo(() => getCinemaStyles(palette), [palette]);

  const [displayedPhaseIdx, setDisplayedPhaseIdx] = useState(phaseIdx);
  const [stepVisible, setStepVisible] = useState(true);
  const [tipIdx, setTipIdx] = useState(0);
  const [tipVisible, setTipVisible] = useState(true);
  const [profileOpen, setProfileOpen] = useState(false);
  const [avatarImgFailed, setAvatarImgFailed] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(
    () => !!document.fullscreenElement,
  );

  // Read by the markup and by the leave timer below, so the two cannot drift apart. Entry is always asked
  // for — an automatic one used to hand the user a scene that was still assembling itself, and made the
  // button a thing only some sessions ever showed. `done` is the whole loading, UE's own readiness included
  // (useCinemaProgress), so there is nothing left to wait for by the time this button exists. A reconnect is
  // the one exception: the user entered already, and the resumer restarts the stream by itself.
  const entryPromptVisible = done && !error && !playStream && !reconnecting;

  const tipSwapTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const barRef = useRef<HTMLDivElement | null>(null);
  const barWidthRef = useRef(0);
  const fillRefs = useRef<(HTMLDivElement | null)[]>([]);
  const headRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (document.getElementById(CINEMA_FONTS_LINK_ID)) {
      return;
    }

    const link = document.createElement('link');
    link.id = CINEMA_FONTS_LINK_ID;
    link.rel = 'stylesheet';
    link.href = CINEMA_FONTS_URL;
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    setShowControls(false);

    return () => {
      setShowControls(true);
    };
  }, []);

  useEffect(() => {
    if (phaseIdx === displayedPhaseIdx) {
      return;
    }

    setStepVisible(false);
    const timeout = setTimeout(() => {
      setDisplayedPhaseIdx(phaseIdx);
      setStepVisible(true);
    }, STEP_FADE_MS);

    return () => clearTimeout(timeout);
  }, [phaseIdx, displayedPhaseIdx]);

  useEffect(() => {
    if (done || error) {
      return;
    }

    const interval = setInterval(() => {
      setTipVisible(false);
      tipSwapTimeoutRef.current = setTimeout(() => {
        setTipIdx((idx) => (idx + 1) % CINEMA_TIPS.length);
        setTipVisible(true);
      }, TIP_FADE_MS);
    }, TIP_ROTATE_MS);

    return () => {
      clearInterval(interval);
      if (tipSwapTimeoutRef.current) {
        clearTimeout(tipSwapTimeoutRef.current);
      }
    };
  }, [done, error]);

  useEffect(() => {
    const handleChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleChange);
    };
  }, []);

  useEffect(() => {
    const bar = barRef.current;

    if (!bar) {
      return;
    }

    const measure = () => {
      barWidthRef.current = bar.clientWidth;
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(bar);

    return () => observer.disconnect();
  }, []);

  const paintProgress = useCallback((value: number) => {
    CINEMA_SEGMENTS.forEach((segment, i) => {
      const fill = fillRefs.current[i];

      if (!fill) {
        return;
      }

      const fraction = Math.max(
        0,
        Math.min(1, (value - segment.start) / (segment.end - segment.start)),
      );
      fill.style.transform = `scaleX(${fraction})`;
    });

    if (headRef.current) {
      const x = (value / 100) * barWidthRef.current;
      headRef.current.style.transform = `translate3d(${x}px, -50%, 0)`;
    }
  }, []);

  useEffect(() => subscribe(paintProgress), [subscribe, paintProgress]);

  // An unanswered prompt is not free: the tab keeps renewing, so the seat stays held until it
  // goes away. Leaving returns it, and the reason is what tells the counters this was no failure.
  useEffect(() => {
    if (!entryPromptVisible) {
      return;
    }

    const leaveTimeout = setTimeout(() => {
      MytaverseLogger.log('Entry prompt unanswered, leaving the experience', {
        eventId: currentEvent?.eventId,
        waitedMs: entryPromptTimeout,
      });

      // Explicit rather than incidental: the empty message already skips the failure record today,
      // but adding a message later must not start burying this expected ending among real failures.
      navigateToEventsPage({
        reason: LeaveEventReason.EntryNotConfirmed,
        recordFailure: false,
      });
    }, entryPromptTimeout);

    return () => clearTimeout(leaveTimeout);
  }, [entryPromptVisible, currentEvent]);

  const handleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
      return;
    }

    document.documentElement.requestFullscreen?.();
  }, []);

  const handleLeave = useCallback(() => {
    navigateToEventsPage({ reason: LeaveEventReason.UserNavigation });
  }, [navigateToEventsPage]);

  const initials = useMemo(() => {
    const fullName =
      [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
      user?.name ||
      user?.email ||
      '';

    return ParticipantService.buildInitials(fullName) ?? '';
  }, [user]);

  const avatarUrl = user?.profileImage?.url;

  const trackStyles = useMemo(
    () =>
      CINEMA_SEGMENTS.map((segment) => ({
        ...classes.segTrack,
        left: segment.left,
        right: segment.right,
      })),
    [classes],
  );

  const phases = reconnecting ? RECONNECT_PHASES : CINEMA_PHASES;
  // The entry ladder speaks first whenever it has something to say (entryWatch.ts): it knows which step this
  // is, why it is taking the time it takes, and whether the page is about to do something about it. The
  // generic copy stays underneath it as the fallback for the states it does not name.
  const headline = error
    ? error.headline
    : done
    ? reconnecting
      ? RECONNECT_READY_HEADLINE
      : READY_HEADLINE
    : entryNotice
    ? entryNotice.title
    : reconnecting
    ? RECONNECT_HEADLINE
    : LOADING_HEADLINE;
  const detail = !error && !done ? entryNotice?.detail ?? null : null;
  const hint = done
    ? reconnecting
      ? RECONNECT_READY_HINT
      : READY_HINT
    : hintOverride ??
      entryNotice?.reassurance ??
      (reconnecting ? RECONNECT_HINT : KEEP_TAB_HINT);
  const tip = CINEMA_TIPS[tipIdx];

  return (
    <Box className="myta-cinema" sx={classes.root}>
      <GlobalStyles styles={cinemaGlobalStyles} />

      <Box sx={classes.media}>
        <Media loadingAsset={currentEvent?.loadingScreenAsset} />
      </Box>

      <Box sx={classes.topBlur} />
      <Box sx={classes.topScrim} />
      <Box sx={classes.railBlur} />
      <Box sx={classes.railScrim} />

      {appTheme?.customerLogo ? (
        <Box sx={classes.logoWrap}>
          {typeof appTheme.customerLogo === 'string' ? (
            <Box
              component="img"
              src={appTheme.customerLogo}
              alt=""
              sx={classes.logoImg}
            />
          ) : (
            <Box sx={classes.logoNode}>{appTheme.customerLogo}</Box>
          )}
        </Box>
      ) : null}

      <Box sx={classes.sys}>
        <Box
          component="button"
          sx={classes.sysBtn}
          onClick={handleFullscreen}
          title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
        >
          {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
        </Box>
        {user ? (
          <Box
            component="button"
            sx={classes.avatar}
            onClick={() => setProfileOpen(true)}
            title="Profile"
            data-testid="myta-cinema-profile-btn"
          >
            {avatarUrl && !avatarImgFailed ? (
              <Box
                component="img"
                src={avatarUrl}
                alt=""
                sx={classes.avatarImg}
                onError={() => setAvatarImgFailed(true)}
              />
            ) : (
              initials || <PersonIcon />
            )}
          </Box>
        ) : null}
        <Box
          component="button"
          sx={classes.sysBtn}
          onClick={handleLeave}
          title="Leave"
        >
          <LogoutIcon />
        </Box>
      </Box>

      <UserProfileModal
        isOpen={profileOpen}
        onClose={() => setProfileOpen(false)}
      />

      <Box sx={classes.rail}>
        {!error ? (
          <Box sx={classes.dock}>
            <ControlsGuide classes={classes} />
            <Box sx={classes.dockSlot}>
              {!done ? (
                <Box sx={{ ...classes.tip, opacity: tipVisible ? 1 : 0 }}>
                  <Typography component="span" sx={classes.tipTag}>
                    TIP
                  </Typography>
                  <Typography component="span" sx={classes.tipText}>
                    {tip.before}
                    <b>{tip.strong}</b>
                    {tip.after}
                  </Typography>
                </Box>
              ) : null}
              {entryPromptVisible ? (
                <Box
                  component="button"
                  sx={classes.cta}
                  onClick={togglePlayStream}
                  data-testid="myta-cinema-enter-btn"
                >
                  Enter experience
                  <ArrowForwardIcon />
                </Box>
              ) : null}
            </Box>
          </Box>
        ) : null}

        <Box sx={classes.statusRow}>
          <Box sx={classes.left}>
            {onSpot && !error ? (
              <Box sx={classes.econ} data-testid="myta-cinema-spot-notice">
                <SavingsOutlinedIcon />
                <Box>
                  <Typography component="span" sx={classes.econTag}>
                    {SPOT_NOTICE_TAG}
                  </Typography>
                  <Typography component="span" sx={classes.econText}>
                    {SPOT_NOTICE_TEXT.before}
                    <b>{SPOT_NOTICE_TEXT.strong}</b>
                    {SPOT_NOTICE_TEXT.after}
                  </Typography>
                </Box>
              </Box>
            ) : null}
            <Box sx={classes.stepLine}>
              <Box
                sx={
                  done || error
                    ? { ...classes.stepDot, ...classes.stepDotStatic }
                    : classes.stepDot
                }
              />
              <Typography
                component="span"
                sx={{ ...classes.stepName, opacity: stepVisible ? 1 : 0 }}
              >
                {error
                  ? error.reason
                  : done && !reconnecting
                  ? READY_STEP
                  : phases[displayedPhaseIdx].label}
              </Typography>
              {!error ? (
                <Typography component="span" sx={classes.stepCnt}>
                  step {displayedPhaseIdx + 1} of {CINEMA_PHASES.length}
                </Typography>
              ) : null}
            </Box>
            <Typography component="h1" sx={classes.headline}>
              {headline}
            </Typography>
            {detail ? (
              <Typography
                component="p"
                sx={classes.detail}
                data-testid="myta-cinema-detail"
              >
                {detail}
              </Typography>
            ) : null}
            {!error ? (
              <Box sx={classes.hint}>
                <InfoOutlinedIcon />
                <span>{hint}</span>
              </Box>
            ) : null}
          </Box>

          {!error ? (
            <Box sx={classes.right}>
              <Box sx={classes.pct}>
                {percent}
                <small>%</small>
              </Box>
              {!done ? (
                <Typography sx={classes.eta}>
                  {etaSec === null ? (
                    ETA_IMMINENT_LABEL
                  ) : (
                    <>
                      ready in ≈ <b>{formatEta(etaSec)}</b>
                    </>
                  )}
                </Typography>
              ) : null}
            </Box>
          ) : null}
        </Box>
      </Box>

      <Box sx={classes.bar} ref={barRef}>
        {CINEMA_SEGMENTS.map((segment, i) => (
          <Box key={segment.label} sx={trackStyles[i]}>
            <Box
              sx={classes.segFill}
              ref={(node: HTMLDivElement | null) => {
                fillRefs.current[i] = node;
              }}
            />
          </Box>
        ))}
        {!done && !error ? <Box sx={classes.head} ref={headRef} /> : null}
      </Box>
    </Box>
  );
};

export default LoadingCinema;
